import { Link } from "@mui/material";

const FourWays = () => {
  return (
    <div className="container--blog_post_page_wrapper">
      <section className="container--blog_post">
        <div className="container--post_content">
          <p className="text--post_paragraph">
            In today's workplace, employee motivation is critical for success.
            Not only is a motivated organization less likely to experience
            turnover and conflict, but it's more likely to perform at a higher
            level and achieve goals.
          </p>
          <p className="text--post_paragraph">
            The reason is simple. When people are motivated, they're engaged.
            And when they're engaged, they're likely to approach their work with
            greater intensity.(
            <Link
              href="https://scholar.google.com/scholar_lookup?title=Work+Motivation+in+Organizational+Behavior&author=C.+C.+Pinder&publication_year=2008&"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read more about Work Motivation in Organizational Behavior by C. C. Pinder"
            >
              Pinder
            </Link>
            ) They take a genuine, passionate interest and put forth a strong
            effort that not only accomplishes the task but makes way for
            innovation and improvement.(
            <Link
              href="https://continuinged.isl.in.gov/wp-content/uploads/2019/06/More_than_Motivation_Monday_2019.pdf"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read more on employee engagement and innovation by Rubin"
            >
              Rubin
            </Link>
            )
          </p>
          <p className="text--post_paragraph">
            But, beware. Last year, Gallup reported a historically massive drop
            in employee engagement. (
            <Link
              href="https://www.gallup.com/workplace/313313/historic-drop-employee-engagement-follows-record-rise.aspx"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read about the historic drop in employee engagement on Gallup"
            >
              Gallup
            </Link>
            ) So you can’t simply assume everyone is engaged and motivated by
            default. Smart leaders must be proactive and do the things that stir
            up fire within their team’s spirits.
          </p>
          <h2 className="header header--page_title_h2_left">
            What does motivate people?
          </h2>
          <p className="text--post_paragraph">
            So exactly what are those things that create an organic, burning
            desire from within? What is it that creates lasting motivation that
            doesn’t fizzle out? Well, let’s get this one out of the way — it’s
            not external incentives like money and financial compensation. The
            key to high performance is to create a workplace where people’s
            internal needs are met.
          </p>
          <p className="text--post_paragraph">
            These internal needs include the desire to connect with others, be
            self-directed, and work on something meaningful. And an
            organizational structure that prioritizes these intrinsic motivators
            as{" "}
            <Link
              href="https://www.teammateme.com/blog/building-better-team-culture"
              target="_blank"
              rel="noreferrer noopener dofollow"
              aria-label="Learn more about intrinsic motivators in workplace culture on TeammateMe"
            >
              essential elements of their workplace culture
            </Link>{" "}
            is more likely to have a workforce that’s fulfilled, motivated, and
            engaged.
          </p>
          <p className="text--post_paragraph">
            In 2018, the Office of Financial Management, State Human Resources,
            developed the RAMP model, a human-centered framework inspired by
            scientific studies like Self-Determination Theory and Maslow's
            Hierarchy of Needs, to identify the true drivers of motivation. The
            RAMP model has four components — relationships, autonomy, mastery,
            and purpose — that organizations can use to create policies that
            motivate. (
            <Link
              href="https://ofm.wa.gov/sites/default/files/public/shr/RAMP%20Handout.pdf"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read about the RAMP model for employee motivation by the Washington State Office of Financial Management"
            >
              Washington State Office of Financial Management
            </Link>
            )
          </p>
          <h2 className="header header--page_title_h2_left">
            1. Relationships: Create Inclusive and Respectful Environments
          </h2>
          <p className="text--post_paragraph">
            Humans have a natural desire to feel connected and respected.
            Healthy social connections not only help us survive but thrive.
            These positive connections, which include the ones we have in the
            workplace, increase our physical and emotional well-being. Having
            meaningful relationships with others gives us a sense of value, as
            well as support and encouragement. s School Strategy
          </p>
          <p className="text--post_paragraph">
            Promoting relationships in the workplace requires a high level of
            trust and communication. Acknowledgment and recognition must be
            built into communication standards. People must know their efforts
            are valued. Even a simple "thank you" goes a long way. Surveys show
            that people often disengage when they don't feel respected or
            treated fairly. So, acknowledge often and create opportunities to
            build social bonds.
          </p>
          <h2 className="header header--page_title_h2_left">
            2. Autonomy: Enable and Empower People to Do Their Best Work
          </h2>
          <p className="text--post_paragraph">
            Nobody likes to be micromanaged. And people with the potential to
            operate at a high level can only do so when given space and
            autonomy. Autonomy allows people to feel a sense of responsibility.
            And when it's combined with feedback and recognition, it makes
            incredible use of people's strengths.
          </p>
          <p className="text--post_paragraph">
            You have to start by assuming that your team will use their time
            wisely and efficiently to complete their tasks. If they don't,
            that's a separate conversation. Build a framework where expectations
            are clearly defined and get out of the way so people can do their
            thing. If you focus on empowering and encouraging, your team will
            discover creative solutions to achieve goals.
          </p>
          <h2 className="header header--page_title_h2_left">
            3. Mastery: Facilitate Career and Personal Growth
          </h2>
          <p className="text--post_paragraph">
            The world is changing at a rapid pace, and the only way for
            organizations and individuals to achieve and maintain a competitive
            advantage is to never stop learning.(
            <Link
              href="https://files.eric.ed.gov/fulltext/EJ1096700.pdf"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
            >
              Singh
            </Link>
            ) Our workplace must encourage continuous skill development. The
            quest for knowledge and mastery of a field or subject is fueled by
            curiosity, the desire for challenge, and the pursuit of recognition.
          </p>
          <p className="text--post_paragraph">
            Create a culture of learning within your organization. Continuing
            education, workshops, lunch-and-learns, and attending industry
            conferences are just a few ways to stay at the forefront of the
            changes and developments within your industry while tapping into
            people's internal desire to grow and evolve.
          </p>
          <h2 className="header header--page_title_h2_left">
            4. Purpose: Define and Live By the "Why"
          </h2>
          <p className="text--post_paragraph">
            We're humans, not machines. And for our passions to be sparked and
            our performance to be stellar, we must operate with meaning. If the
            "why" isn't understood and in tune with our internal values, we
            won’t feel motivated to reach new heights. Not only that, but
            purpose is the touchstone that helps us regain clarity and focus
            when disruptions occur.
          </p>
          <p className="text--post_paragraph">
            Purpose is a lot more than a mission statement (although they're a
            good start). You have to clearly identify what you’re working to
            achieve and show your team how, where, and why their work matters
            with quantifiable success metrics. When you stimulate the innate
            drive for purpose, it can produce the highest levels of motivation.
          </p>
          <h2 className="header header--page_title_h2_left">
            Wait! What About Money?
          </h2>
          <p className="text--post_paragraph">
            It's more effective for managers to look toward the RAMP model to
            build motivation rather than assuming financial incentives will do
            it. Research shows that monetary rewards, performance bonuses,
            promotions, and other external motivators can be counterproductive
            when the work involves creativity and cognitive abilities.(
            <Link
              href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC7046595/"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
            >
              Shkoler & Kimura
            </Link>
            )
          </p>
          <p className="text--post_paragraph">
            With that said, organizations need to establish the foundation of
            employee-employer relationships with competitive compensation
            packages. If basic life needs aren’t met, we can’t approach work
            with an enthusiastic frame of mind. And, in fact, if compensation
            and working conditions aren't acceptable, they become significant
            demotivating factors.
          </p>
          <h2 className="header header--page_title_h2_left">
            RAMP Up Your Team's Motivation
          </h2>
          <p className="text--post_paragraph">
            Creating a motivated workforce doesn't have to involve hanging
            posters with hokey inspirational phrases or listening to self-help
            coaches who yell too much. Developing a culture that promotes RAMP —
            relationships, autonomy, mastery, and purpose — will inspire
            high-performance organically.
          </p>
          <p className="text--post_paragraph">
            Make people feel valued, respected, and heard, regardless of their
            position. Provide ongoing opportunities for learning and growth. And
            take time to regularly acknowledge how everyone's contributions are
            vital to the organization's success. When you lead this way, your
            team will be motivated and on track for greatness.
          </p>
        </div>
        <hr className="hr--form_break" />
      </section>
    </div>
  );
};

export default FourWays;
